import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  tDicomAbstractName,
  tDicomAbstractDescription,
  tDicomTransferSyntaxName,
} from '../../../../helpers/i18n/dicomSyntaxI18n';
import type {
  ApplicationEntity,
  Device,
  Connection,
  DicomSyntaxConfig,
  AbstractSyntaxConfig,
  TransferSyntaxValue,
} from '../types/dicom';
import {
  Modal,
  Button,
  Input,
  Badge,
} from '../custom-components/custom-components';
import { Select } from '../../../ComponnentsUI/common/Select';
import {
  abstractSyntaxInfo,
  transferSyntaxInfo,
} from '../resources/dicom-config';
import { getAcceptedCount } from '../utils/dicom-helpers';
import {
  useUpdateComprehensiveApplicationEntity,
  type ComprehensiveAEUpdateData,
} from '../../../../helpers/api/pacs/pacs';
import { messageI18n } from '../../../../helpers/i18n/toastI18n';

interface AEFormData {
  aeTitle: string;
  name: string;
  description: string;
  aeType: string;
}

interface ConfigureAEModalProps {
  isOpen: boolean;
  onClose: () => void;
  ae: ApplicationEntity | null;
  device: Device | null;
  connection: Connection | null;
  onUpdate: (
    device: Device,
    connection: Connection,
    ae: ApplicationEntity,
    formData: AEFormData
  ) => void;
  onSyntaxUpdate: (
    deviceId: string,
    connectionId: string,
    aeId: string,
    syntaxConfig: DicomSyntaxConfig
  ) => void;
}

export const ConfigureAEModal: React.FC<ConfigureAEModalProps> = ({
  isOpen,
  onClose,
  ae,
  device,
  connection,
  onUpdate,
  onSyntaxUpdate,
}) => {
  const { t } = useTranslation('Organization');
  const [aeFormData, setAeFormData] = useState<AEFormData>({
    aeTitle: ae?.aeTitle || '',
    name: ae?.name || '',
    description: ae?.description || '',
    aeType: ae?.dicomType || '', // Handle null/empty as Mix
  });

  // Local syntax config state for the modal
  const [localSyntaxConfig, setLocalSyntaxConfig] =
    useState<DicomSyntaxConfig | null>(null);

  // Hook for comprehensive AE update
  const updateComprehensiveAE = useUpdateComprehensiveApplicationEntity();

  // Transform syntax config to transfer capabilities format
  // ALWAYS includes ALL SOP classes - never omits any
  // Uses empty transferSyntaxes string to signal deletion
  const transformSyntaxConfigToTransferCapabilities = (
    syntaxConfig: DicomSyntaxConfig
  ): ComprehensiveAEUpdateData['transferCapabilities'] => {
    const transferCapabilities: ComprehensiveAEUpdateData['transferCapabilities'] =
      [];

    Object.entries(syntaxConfig).forEach(
      ([abstractSyntaxKey, transferSyntaxes]) => {
        const abstractSyntax =
          abstractSyntaxInfo[
            abstractSyntaxKey as keyof typeof abstractSyntaxInfo
          ];
        if (!abstractSyntax) {
          return;
        }

        // Get accepted transfer syntaxes
        const acceptedTransferSyntaxes = Object.entries(transferSyntaxes)
          .filter(([_, value]) => value === 'accepted')
          .map(([transferSyntaxKey]) => {
            const transferSyntax =
              transferSyntaxInfo[
                transferSyntaxKey as keyof typeof transferSyntaxInfo
              ];
            return transferSyntax?.uid;
          })
          .filter(Boolean);

        // ALWAYS add the SOP class to the array
        // If no transfer syntaxes are accepted, use empty string to signal deletion
        transferCapabilities.push({
          sopClass: abstractSyntax.sopClassUid,
          role: 'SCP',
          transferSyntaxes:
            acceptedTransferSyntaxes.length > 0
              ? acceptedTransferSyntaxes.join(',')
              : '', // Empty string signals deletion
          commonName: abstractSyntax.name,
          enabled: true,
          priority: 0,
        });
      }
    );

    return transferCapabilities;
  };

  // Update form data and syntax config when modal opens or AE changes
  useEffect(() => {
    if (isOpen && ae) {
      setAeFormData({
        aeTitle: ae.aeTitle || '',
        name: ae.name || '',
        description: ae.description || '',
        aeType: ae.dicomType || '', // Handle null/empty as Mix
      });
      // Create a deep copy of the syntax config
      setLocalSyntaxConfig(JSON.parse(JSON.stringify(ae.syntaxConfig)));
    }
  }, [isOpen, ae]);

  const handleUpdate = async () => {
    // Check if AE is running (safety check)
    if (ae && ae.status === 'running') {
      messageI18n.error('devices.cannotUpdateActiveAe');
      return;
    }

    if (
      device &&
      connection &&
      ae &&
      aeFormData.aeTitle.trim() &&
      aeFormData.name.trim() &&
      localSyntaxConfig
    ) {
      try {
        // Transform syntax config to transfer capabilities
        const transferCapabilities =
          transformSyntaxConfigToTransferCapabilities(localSyntaxConfig);

        const getAeType = () => {
          if (!aeFormData.aeType) {
            return null;
          }
          return aeFormData.aeType.toUpperCase() === 'STANDARD'
            ? 'OB'
            : 'BLIND';
        };

        // Prepare comprehensive update data
        const updateData: ComprehensiveAEUpdateData = {
          aeTitle: aeFormData.aeTitle,
          name: aeFormData.name,
          description: aeFormData.description,
          aeType: getAeType(),
          transferCapabilities,
        };

        // Call comprehensive update API
        await updateComprehensiveAE.mutateAsync({
          aeId: parseInt(ae.id),
          aeData: updateData,
        });

        // Update local state for immediate UI feedback
        onUpdate(device, connection, ae, aeFormData);
        onSyntaxUpdate(device.id, connection.id, ae.id, localSyntaxConfig);
        onClose();
      } catch (error) {
        console.error('Failed to update AE:', error);
        // You might want to show an error message to the user here
      }
    }
  };

  const handleBulkUpdate = (
    abstractSyntax: keyof DicomSyntaxConfig,
    value: TransferSyntaxValue
  ) => {
    if (!localSyntaxConfig) {
      return;
    }

    const updatedConfig = { ...localSyntaxConfig };
    Object.keys(updatedConfig[abstractSyntax]).forEach((transferSyntax) => {
      updatedConfig[abstractSyntax][
        transferSyntax as keyof AbstractSyntaxConfig
      ] = value;
    });
    setLocalSyntaxConfig(updatedConfig);
  };

  const handleSyntaxChange = (
    abstractSyntax: keyof DicomSyntaxConfig,
    transferSyntax: string,
    value: TransferSyntaxValue
  ) => {
    if (!localSyntaxConfig) {
      return;
    }

    const updatedConfig = { ...localSyntaxConfig };
    updatedConfig[abstractSyntax][
      transferSyntax as keyof AbstractSyntaxConfig
    ] = value;
    setLocalSyntaxConfig(updatedConfig);
  };

  const handleCancel = () => {
    // Reset local syntax config to original values
    if (ae) {
      setLocalSyntaxConfig(JSON.parse(JSON.stringify(ae.syntaxConfig)));
      setAeFormData({
        aeTitle: ae.aeTitle || '',
        name: ae.name || '',
        description: ae.description || '',
        aeType: ae.dicomType || '',
      });
    }
    onClose();
  };

  if (!ae) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('devices.modals.configureAeTitle')}
      description={t('devices.modals.configureAeDescription', { aeTitle: ae.aeTitle })}
      size="xl"
    >
      <div className="space-y-6">
        {/* Basic Configuration */}
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              {t('devices.modals.configureAe.basicConfig')}
            </h3>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                {t('devices.modals.configureAe.name')} *
              </label>
              <Input
                value={aeFormData.name}
                onChange={(e) =>
                  setAeFormData({ ...aeFormData, name: e.target.value })
                }
                placeholder={t('devices.modals.aeNamePlaceholder')}
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('devices.modals.configureAe.nameHint')}
              </p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                {t('devices.modals.configureAe.aeTitle')} *
              </label>
              <Input
                value={aeFormData.aeTitle}
                onChange={(e) =>
                  setAeFormData({ ...aeFormData, aeTitle: e.target.value })
                }
                placeholder={t('devices.modals.aeTitlePlaceholder')}
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('devices.modals.configureAe.aeTitleHint')}
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                {t('devices.modals.description')}
              </label>
              <Input
                value={aeFormData.description}
                onChange={(e) =>
                  setAeFormData({ ...aeFormData, description: e.target.value })
                }
                placeholder={t('devices.modals.aeDescriptionPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                {t('devices.modals.configureAe.aeType')}
              </label>
              <div className="grid grid-cols-3 gap-3">
                <div
                  className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                    aeFormData.aeType === ''
                      ? 'border-gray-400 bg-gray-50 dark:bg-gray-700'
                      : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setAeFormData({ ...aeFormData, aeType: '' })}
                >
                  <input
                    type="radio"
                    value=""
                    checked={aeFormData.aeType === ''}
                    onChange={() =>
                      setAeFormData({ ...aeFormData, aeType: '' })
                    }
                    className="text-gray-600"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('pacsTab.table.mix')}
                  </span>
                </div>
                <div
                  className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                    aeFormData.aeType === 'standard'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                  onClick={() =>
                    setAeFormData({ ...aeFormData, aeType: 'standard' })
                  }
                >
                  <input
                    type="radio"
                    value="standard"
                    checked={aeFormData.aeType === 'standard'}
                    onChange={() =>
                      setAeFormData({ ...aeFormData, aeType: 'standard' })
                    }
                    className="text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('devices.modals.configureAe.standard')}
                  </span>
                </div>
                <div
                  className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                    aeFormData.aeType === 'blind'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                  onClick={() =>
                    setAeFormData({ ...aeFormData, aeType: 'blind' })
                  }
                >
                  <input
                    type="radio"
                    value="blind"
                    checked={aeFormData.aeType === 'blind'}
                    onChange={() =>
                      setAeFormData({ ...aeFormData, aeType: 'blind' })
                    }
                    className="text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('devices.modals.configureAe.blind')}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('devices.modals.configureAe.examTypeHint')}
              </p>
            </div>
          </div>
        </div>

        {/* Transfer Syntax Configuration */}
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              {t('devices.modals.configureAe.transferSyntax')}
            </h3>
            <p className="text-sm text-slate-600 dark:text-gray-400">
              {t('devices.modals.configureAe.transferSyntaxHint')}
            </p>
          </div>
          <div className="p-4 space-y-4">
            {Object.entries(abstractSyntaxInfo).map(([key, info]) => {
              const abstractKey = key as keyof DicomSyntaxConfig;
              const config =
                localSyntaxConfig?.[abstractKey] ||
                ae.syntaxConfig[abstractKey];
              const acceptedCount = localSyntaxConfig
                ? getAcceptedCount(abstractKey, localSyntaxConfig)
                : getAcceptedCount(abstractKey, ae.syntaxConfig);
              const totalCount = Object.keys(config).length;

              return (
                <div
                  key={key}
                  className="border border-slate-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-slate-900 dark:text-white">
                          {tDicomAbstractName(key)}
                        </h4>
                        <p className="text-xs text-slate-600 dark:text-gray-400">
                          {tDicomAbstractDescription(key)}
                        </p>
                        <div className="text-xs text-slate-500 dark:text-gray-500 font-mono mt-1">
                          {info.sopClassUid}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            acceptedCount > 0
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }
                        >
                          {t('devices.modals.configureAe.acceptedCount', {
                            accepted: acceptedCount,
                            total: totalCount,
                          })}
                        </Badge>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleBulkUpdate(abstractKey, 'accepted')
                            }
                            className="text-green-600 border-green-200 hover:bg-green-50 text-xs px-2 py-1"
                          >
                            {t('devices.common.acceptAll')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleBulkUpdate(abstractKey, 'rejected')
                            }
                            className="text-red-600 border-red-200 hover:bg-red-50 text-xs px-2 py-1"
                          >
                            {t('devices.common.rejectAll')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="space-y-3">
                      {/* {t('devices.common.uncompressed')} Transfer Syntaxes */}
                      <div>
                        <h5 className="text-xs font-medium text-slate-700 dark:text-gray-400 mb-2">
                          {t('devices.common.uncompressed')}
                        </h5>
                        <div className="grid grid-cols-1 gap-2">
                          {Object.entries(transferSyntaxInfo)
                            .filter(([_, info]) => info.type === 'Uncompressed')
                            .map(([transferKey, transferInfo]) => {
                              if (!(transferKey in config)) {
                                return null;
                              }
                              const value =
                                config[
                                  transferKey as keyof AbstractSyntaxConfig
                                ];
                              return (
                                <div
                                  key={transferKey}
                                  className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                                >
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-slate-900 dark:text-white">
                                      {tDicomTransferSyntaxName(transferKey)}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                                      {transferInfo.uid}
                                    </div>
                                  </div>
                                  <Select
                                    value={value}
                                    onChange={(e) =>
                                      handleSyntaxChange(
                                        abstractKey,
                                        transferKey,
                                        e.target.value as TransferSyntaxValue
                                      )
                                    }
                                    className="w-20 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    options={[
                                      { value: 'accepted', label: t('devices.modals.configureAe.accept') },
                                      { value: 'rejected', label: t('devices.modals.configureAe.reject') },
                                    ]}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      {/* {t('devices.common.compressed')} Transfer Syntaxes */}
                      <div>
                        <h5 className="text-xs font-medium text-slate-700 dark:text-gray-400 mb-2">
                          {t('devices.modals.configureAe.compressed')}
                        </h5>
                        <div className="grid grid-cols-1 gap-2">
                          {Object.entries(transferSyntaxInfo)
                            .filter(([_, info]) => info.type === 'Compressed')
                            .map(([transferKey, transferInfo]) => {
                              if (!(transferKey in config)) {
                                return null;
                              }
                              const value =
                                config[
                                  transferKey as keyof AbstractSyntaxConfig
                                ];
                              return (
                                <div
                                  key={transferKey}
                                  className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                                >
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-slate-900 dark:text-white">
                                      {tDicomTransferSyntaxName(transferKey)}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                                      {transferInfo.uid}
                                    </div>
                                  </div>
                                  <Select
                                    value={value}
                                    onChange={(e) =>
                                      handleSyntaxChange(
                                        abstractKey,
                                        transferKey,
                                        e.target.value as TransferSyntaxValue
                                      )
                                    }
                                    className="w-20 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    options={[
                                      { value: 'accepted', label: t('devices.modals.configureAe.accept') },
                                      { value: 'rejected', label: t('devices.modals.configureAe.reject') },
                                    ]}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={handleCancel}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleUpdate}
          disabled={
            !aeFormData.aeTitle.trim() ||
            !aeFormData.name.trim() ||
            ae?.status === 'running'
          }
          className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            ae?.status === 'running'
              ? t('devices.modals.configureAe.cannotUpdateRunning')
              : ''
          }
        >
          {ae?.status === 'running'
            ? t('devices.modals.configureAe.aeRunning')
            : t('devices.modals.configureAe.updateAe')}
        </Button>
      </div>
    </Modal>
  );
};
